import { el, clear } from '../../core/dom.js';
import { withProduct } from '../../core/route-map.js';
import { button } from '../../components/button.js';
import { empty } from '../../components/empty.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import {
  spanFor, rowsFor, sortedWidgets, widgetMeta, layoutFromPreset,
  moveWidget, removeWidget, addWidget, resizeWidget, WIDGETS, SECTION_IDS,
} from './layout.js';
import { loadDashboard } from './load.js';
import {
  kpiCard, insightsCard, depthCard, setupCard, reportsCard, connectorsCard, chatsCard,
} from './cards.js';
import {
  isPhone, layoutFromSaved, saveLayout, renderEditBar, renderPhonePresets,
  renderModal, toolRow, bindDrag,
} from './editor.js';
import {
  sectionCard, attentionCard, hiddenLine, topKpiFromBlock, topDepthFromBlock, isSectionId,
} from './sections.js';
import { api } from '../../core/api.js';
import { digestPrefBody, seenBody } from '../../core/parity.js';
import { syncNowButton } from '../../components/sync-now.js';

const KPI_IDS = new Set(['mau', 'act', 'd7', 'paid']);
const TOP_CODES = { mau: 'KPI-01', act: 'KPI-02', d7: 'KPI-03' };

function isAdmin(opts) {
  return !!(opts.me && opts.me.role === 'admin');
}

function widgetNode(id, opts, data, reload) {
  const v2 = data.dashboard_v2 && data.v2;
  if (v2 && KPI_IDS.has(id) && id !== 'paid') {
    const block = (v2.top_row || []).find((b) => b && b.code === TOP_CODES[id]);
    if (block) return topKpiFromBlock(block, opts.productId);
  }
  if (v2 && id === 'depth') {
    const block = (v2.top_row || []).find((b) => b && b.type === 'depth');
    if (block) return topDepthFromBlock(block, opts.productId, depthCard);
  }
  if (v2 && id === 'attention') return attentionCard(v2, opts.productId);
  if (v2 && isSectionId(id)) return sectionCard(id, v2, opts.productId);

  if (KPI_IDS.has(id)) return kpiCard(id, data.insights, opts.productId);
  if (id === 'insights') return insightsCard(data.insights, reload, opts.productId);
  if (id === 'depth') return depthCard(data.depth, opts.productId);
  if (id === 'setup') return setupCard(data.insights, opts.productId);
  if (id === 'reports') return reportsCard(data.schedules, opts.productId);
  if (id === 'conns') return connectorsCard(data.connectors, opts.productId, isAdmin(opts));
  if (id === 'chats') return chatsCard(data.chats, opts.productId, opts.productName);
  return el('article', { class: 'dash-w' }, widgetMeta(id).title);
}

function place(node, widget, preset, editing, animate) {
  node.classList.add(`s${spanFor(widget, preset)}`);
  node.classList.add('lift');
  if (animate) node.classList.add('rv');
  if (rowsFor(widget, preset) > 1) node.classList.add('r2');
  node.dataset.wid = widget.id;
  if (editing && !isPhone()) node.prepend(toolRow(widget));
  return node;
}

/**
 * @param {{ productId?: string, productName?: string, me?: object }} opts
 */
export function renderDashboard(opts) {
  const root = el('div', { class: 'dash' });
  const state = {
    layout: layoutFromPreset('overview'),
    editing: false,
    modal: null,
    draft: layoutFromPreset('overview'),
    data: null,
    focusId: '',
    painted: false,
  };
  root.append(skeleton(6));

  function persist() {
    if (!opts.productId) return;
    saveLayout(opts.productId, state.layout).then((result) => {
      if (result && result.error) toast(result.error, 'error');
    });
  }

  function announce(text) {
    const live = root.querySelector('.dash-live');
    if (live) live.textContent = text;
  }

  function rerender(optsRender = {}) {
    if (!state.data) return;
    const animate = !state.painted && optsRender.animate !== false;
    const phone = isPhone();
    const layout = state.layout;
    const reload = () => {
      state.data = null;
      state.painted = false;
      clear(root);
      root.append(skeleton(4));
      load().then(() => rerender({ animate: true }));
    };
    clear(root);
    const depth = Number(state.data.depth && state.data.depth.analysis_depth) || 0;
    const connectors = (state.data.connectors && state.data.connectors.integrations) || [];
    const name = opts.productName || 'This product';
    const sources = connectors.length
      ? `Live readings from ${connectors.length} connected source${connectors.length === 1 ? '' : 's'}.`
      : 'Connect a source and the readings show up here.';
    const actions = editorActions();
    actions.hiddenSections = (state.data.v2 && state.data.v2.hidden) || [];
    const nodes = [
      el('header', { class: 'dash-head' },
        el('div', {},
          el('h1', { class: 'dash-title' }, `${name}, `, el('em', {}, 'at a glance.')),
          el('p', { class: 'dash-copy' }, sources)),
        el('div', { class: 'dash-actions' },
          el('span', { class: 'dash-chip' }, 'Depth ', el('strong', {}, `${depth}%`)),
          digestPill(state.data.insights, () => rerender({ animate: false })),
          isAdmin(opts) && state.data.source_id ? syncNowButton(state.data.source_id) : null,
          button(state.editing ? 'Done editing' : 'Edit', {
            className: state.editing ? 'is-on' : '',
            onClick: () => (phone ? actions.openModal('layout') : actions.toggleEdit()),
          }),
          button('Customise layout', { onClick: () => actions.openModal('layout') }),
          button('Ask Bonnie', { href: withProduct('/app/chat/new', opts.productId) }))),
      phone ? renderPhonePresets(layout, actions) : null,
      state.editing && !phone ? renderEditBar(actions) : null,
      boardNode(reload, animate),
      state.data.dashboard_v2 ? hiddenLine(state.data.v2, opts.productId) : null,
      el('p', { class: 'dash-live', 'aria-live': 'polite' }),
      state.modal ? renderModal(state, actions) : null,
    ];
    root.append(...nodes.filter(Boolean));
    bindDrag(root.querySelector('.dash-grid'), {
      editing: state.editing,
      move: actions.move,
    });
    root.querySelectorAll('[data-size]').forEach((node) => {
      node.addEventListener('click', () => actions.resize(node.getAttribute('data-size')));
    });
    root.querySelectorAll('[data-remove]').forEach((node) => {
      node.addEventListener('click', () => actions.remove(node.getAttribute('data-remove')));
    });
    if (state.focusId) {
      const handle = root.querySelector(`[data-wid="${CSS.escape(state.focusId)}"] .dash-handle`);
      if (handle) handle.focus();
      state.focusId = '';
    }
    state.painted = true;
  }

  function boardNode(reload, animate) {
    const layout = state.layout;
    const visible = visibleWidgets(layout.widgets, state.data);
    const hiddenCount = WIDGETS.length - visible.length;
    const board = el('div', {
      class: state.editing ? 'dash-grid is-editing' : 'dash-grid',
      'aria-label': 'Dashboard widgets',
    });
    if (!visible.length) {
      board.append(empty({
        title: 'Nothing on the board.',
        body: 'Add widgets back or pick a layout.',
        action: button('Add widgets', { variant: 'primary', onClick: () => editorActions().openModal('widgets') }),
      }));
      return board;
    }
    sortedWidgets(visible).forEach((widget) => {
      board.append(place(
        widgetNode(widget.id, opts, state.data, reload),
        widget,
        layout.preset,
        state.editing,
        animate,
      ));
    });
    if (state.editing && !isPhone() && hiddenCount > 0) {
      board.append(el('button', {
        type: 'button',
        class: 'dash-add',
        onClick: () => editorActions().openModal('widgets'),
      }, '+ Add widget', el('span', { class: 'dash-note' }, `${hiddenCount} available`)));
    }
    return board;
  }

  /** Hide empty Needs attention and unavailable sections from the board (still in library). */
  function visibleWidgets(widgets, data) {
    const v2 = data && data.dashboard_v2 && data.v2;
    if (!v2) {
      return (widgets || []).filter((w) => !SECTION_IDS.includes(w.id) && w.id !== 'attention');
    }
    const available = new Set(v2.available_sections || (v2.sections || []).map((s) => s.id));
    const hasAttention = Array.isArray(v2.attention) && v2.attention.length > 0;
    return (widgets || []).filter((w) => {
      if (w.id === 'attention') return hasAttention || state.editing;
      if (SECTION_IDS.includes(w.id)) return available.has(w.id) || state.editing;
      return true;
    });
  }

  function editorActions() {
    return {
      hiddenSections: (state.data && state.data.v2 && state.data.v2.hidden) || [],
      toggleEdit() {
        state.editing = !state.editing;
        announce(state.editing ? 'Editing layout' : 'Layout saved');
        if (!state.editing) persist();
        rerender({ animate: false });
      },
      done() {
        state.editing = false;
        announce('Layout saved');
        persist();
        rerender({ animate: false });
      },
      reset() {
        state.layout = layoutFromPreset(state.layout.preset);
        announce('Layout reset');
        persist();
        rerender({ animate: false });
      },
      openModal(tab) {
        state.modal = tab;
        state.draft = {
          preset: state.layout.preset,
          widgets: state.layout.widgets.map((widget) => ({ ...widget })),
        };
        rerender({ animate: false });
      },
      closeModal() { state.modal = null; rerender({ animate: false }); },
      setTab(tab) { state.modal = tab; rerender({ animate: false }); },
      pickDraft(id) { state.draft = layoutFromPreset(id); rerender({ animate: false }); },
      pickPreset(id) {
        state.layout = layoutFromPreset(id);
        announce(`${state.layout.preset} layout applied`);
        persist();
        rerender({ animate: false });
      },
      toggleDraft(id) {
        const has = state.draft.widgets.some((widget) => widget.id === id);
        state.draft = {
          preset: state.draft.preset,
          widgets: has ? removeWidget(state.draft.widgets, id) : addWidget(state.draft.widgets, id),
        };
        rerender({ animate: false });
      },
      apply(arrange) {
        state.layout = {
          preset: state.draft.preset,
          widgets: state.draft.widgets.map((widget) => ({ ...widget })),
        };
        state.modal = null;
        state.editing = arrange || state.editing;
        announce('Layout applied');
        persist();
        rerender({ animate: false });
      },
      move(id, delta) {
        state.layout = { ...state.layout, widgets: moveWidget(state.layout.widgets, id, delta) };
        state.focusId = id;
        announce(`${widgetMeta(id).title} moved`);
        persist();
        rerender({ animate: false });
      },
      resize(id) {
        state.layout = { ...state.layout, widgets: resizeWidget(state.layout.widgets, id) };
        announce(`${widgetMeta(id).title} resized`);
        persist();
        rerender({ animate: false });
      },
      remove(id) {
        state.layout = { ...state.layout, widgets: removeWidget(state.layout.widgets, id) };
        announce(`${widgetMeta(id).title} removed`);
        persist();
        rerender({ animate: false });
      },
    };
  }

  function load() {
    const pref = opts.productId
      ? api(`/me/ui-prefs/dashboard_layout?product_id=${encodeURIComponent(opts.productId)}`)
      : Promise.resolve(null);
    return Promise.all([loadDashboard(opts.productId, { me: opts.me }), pref]).then(([data, saved]) => {
      state.data = data;
      const available = data.v2 && data.v2.available_sections;
      if (saved && saved.value) {
        state.layout = layoutFromSaved(saved.value, {
          migrate: !!data.dashboard_v2,
          available,
        });
        if (data.dashboard_v2 && state.layout.migrated) persist();
      } else if (data.dashboard_v2) {
        state.layout = layoutFromPreset('overview');
      }
      state.draft = state.layout;
      markInsightsSeen(data && data.insights);
      return data;
    });
  }

  if (!opts.productId) {
    clear(root);
    root.append(empty({
      title: 'Add a product to open the dashboard',
      body: 'KPIs, insights and connector health are per product.',
      action: button('Add a product', { href: '/app/products/new', variant: 'primary' }),
    }));
    return root;
  }

  load().then(() => rerender({ animate: true }));
  root.dataset.phone = isPhone() ? '1' : '0';
  window.addEventListener('resize', () => {
    if (!root.isConnected) return;
    const now = isPhone() ? '1' : '0';
    if (now === root.dataset.phone) return;
    root.dataset.phone = now;
    if (state.data) rerender({ animate: false });
  });
  return root;
}

function digestPill(insights, rerender) {
  if (!insights || insights.error) return null;
  const on = insights.digest_on !== false;
  return el('button', {
    type: 'button',
    class: on ? 'dash-pill' : 'dash-pill is-off',
    onClick: () => toggleDigest(insights, rerender),
  }, on ? 'digest on' : 'digest off');
}

async function toggleDigest(insights, rerender) {
  const next = insights.digest_on === false;
  const result = await api('/insights/digest-pref', { method: 'POST', body: digestPrefBody(next) });
  if (!result || result.error) {
    toast((result && result.error) || 'Could not save digest preference', 'error');
    return;
  }
  insights.digest_on = result.digest_on !== false;
  rerender();
}

function markInsightsSeen(insights) {
  const body = seenBody(insights);
  if (!body) return;
  api('/insights/seen', { method: 'POST', body }).then(() => {});
}
